"use client";

import React, { useEffect, useRef, useState } from "react";
import { Leaf, ArrowRight, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ParadisePalmsExperience() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="150709" id="the-xo-experience" className="relative bg-background py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-primary overflow-hidden">
        <Leaf className="w-[150vw] h-[150vw] md:w-[100vw] md:h-[100vw] -rotate-12 translate-x-1/4 translate-y-1/4" strokeWidth={0.3} />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={imageRef} className={`lg:col-span-7 relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative w-full max-w-2xl mx-auto lg:mx-0 lg:ml-auto">
              {}
              <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-none shadow-none relative z-10 border-[12px] border-secondary">
                <img src="https://warm-lionfish.demandflow.net/wp-content/uploads/2026/04/image_xb1d8je5.jpg" alt="Editorial boudoir photography session in a luxury Minneapolis hotel suite" className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
              </div>
              
              {}
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-accent/20 rounded-none blur-3xl -z-10"></div>
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-muted/30 rounded-none blur-2xl -z-10"></div>
            </div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-5 flex flex-col items-start transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Badge variant="outline" className="mb-6 bg-transparent text-muted-foreground/30 border-none p-0 text-7xl font-serif font-light tracking-tighter">
              01
            </Badge>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6 leading-[1.1] tracking-tight">
              The Luxury <br />
              <span className="text-primary italic font-light">of Comfort</span>
            </h2>
            
            <p className="text-muted-foreground text-lg mb-8 leading-relaxed font-sans tracking-wide">
              XO Boudoir brings a high-end, editorial approach to Minneapolis. Whether in the privacy of your home, a luxury hotel suite, or a professional studio, we capture your most empowered self with sophisticated artistry and absolute discretion.
            </p>

            <ul className="space-y-3 mb-10">
              {["Bespoke on-location luxury service", "Professional lighting & creative direction", "Private, secure digital reveal gallery"].map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-foreground/90 font-sans text-sm uppercase tracking-widest">
                  <CircleCheck className="w-4 h-4 text-primary shrink-0" />
                  <span>{item}</span>
                </li>)}
            </ul>

            {}
            <div className="bg-secondary border-none shadow-none rounded-none p-8 mb-10 w-full max-w-md relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-none -z-10 transition-transform group-hover:scale-110"></div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-[0.2em] mb-2">Location</p>
                  <div className="flex items-baseline gap-1">
                    {}
                    <span className="text-3xl font-serif font-bold text-primary tracking-tight">Minneapolis</span>
                  </div>
                </div>
                
                
              </div>
            </div>

            <Button size="lg" className="rounded-none px-0 h-auto text-base font-medium group shadow-none bg-transparent text-primary hover:bg-transparent border-b border-primary/30 hover:border-primary transition-all duration-300" asChild>
              <Link to="#">
                Learn More
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

        </div>
      </div>
    </section>;
}